import React, { useState, useEffect, useRef } from 'react';
import { X, AlertTriangle, Plus, Trash2, GripVertical, ChevronDown, ChevronRight } from 'lucide-react';
import { useUpdateField, useDeleteField, useFieldsByObject, getFieldTypeLabel, normalizePicklistValues, type CustomField, type PicklistValue } from '../../../services/queries/fieldQueries';
import { useObjects } from '../../../services/queries/objectQueries';
import { FieldSecurityManager } from '../object-manager/FieldSecurityManager';
import { useRecentItems } from '../../../hooks/useRecentItems';
import { useToast } from '../../../contexts/ToastContext';
import { useSetupChanges } from '../../../hooks/useSetupChanges';

interface EditFieldModalProps {
  field: CustomField | null;
  isOpen: boolean;
  onClose: () => void;
  objectId?: string; // Optional: Object ID (e.g., "obj_client") - provided when opened from object manager
}

export const EditFieldModal: React.FC<EditFieldModalProps> = ({
  field,
  isOpen,
  onClose,
  objectId: providedObjectId
}) => {
  const [formData, setFormData] = useState<Partial<CustomField>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteInProgressRef = useRef(false);

  // PE-1470: Picklist values with metadata support
  const [picklistValues, setPicklistValues] = useState<PicklistValue[]>([]);
  const [expandedMetadata, setExpandedMetadata] = useState<Record<number, boolean>>({});

  // Track field ID to prevent unnecessary state resets during refetches
  const initializedFieldIdRef = useRef<string | null>(null);

  const updateFieldMutation = useUpdateField();
  const deleteFieldMutation = useDeleteField();
  const { data: objects } = useObjects();
  const { addRecentItem } = useRecentItems();
  const { success, error } = useToast();
  const { logChange } = useSetupChanges();

  // PE-1470: Get fields for this object (for target field dropdown in metadata)
  const objectIdForFields = providedObjectId || field?.objectId;
  const { data: objectFields } = useFieldsByObject(objectIdForFields || '', {
    includeInactive: false
  });

  // Get the object for this field to extract the objectName (API name)
  // Use provided objectId if available, otherwise fall back to field's objectId
  const objectIdToUse = providedObjectId || field?.objectId;
  const currentObject = objects?.find(obj => obj.id === objectIdToUse);

  const lastAddedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!field || !isOpen || !currentObject) return;
    const normalizedId = field.id.replace(/^field-/, '');
    const dedupeKey = `${normalizedId}`;
    if (lastAddedRef.current === dedupeKey) return;
    lastAddedRef.current = dedupeKey;
    addRecentItem({
      id: normalizedId.startsWith('field_') ? normalizedId : `field_${normalizedId}`,
      title: field.fieldLabel,
      subtitle: `${currentObject.label} • ${getFieldTypeLabel(field.fieldType)} field`,
      path: '/admin?section=object-manager',
      type: 'field',
      icon: 'FileText'
    });
  }, [isOpen, field?.id, field?.fieldLabel, field?.fieldType, currentObject?.label, addRecentItem]);
  useEffect(() => {
    if (!isOpen) {
      lastAddedRef.current = null;
      deleteInProgressRef.current = false;
      // Reset so next open will reinitialize state
      initializedFieldIdRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (field && isOpen) {
      // Only initialize state when opening modal with a new/different field
      // This prevents resetting user edits during background refetches
      if (initializedFieldIdRef.current !== field.id) {
        console.log('[EditFieldModal] Initializing field state:', {
          fieldId: field.id,
          previousFieldId: initializedFieldIdRef.current,
          isOpen
        });
        initializedFieldIdRef.current = field.id;
        setFormData(field);

        // PE-1470: Initialize picklist values with metadata support
        // Also check for 'badge' type since some fields (like Stage) are defined as badge but rendered as picklist
        const isPicklistType = field.fieldType === 'picklist' || field.fieldType === 'multipicklist' || field.fieldType === 'badge';

        console.log('[EditFieldModal] ===== FIELD DATA RECEIVED =====');
        console.log('[EditFieldModal] Full field object:', JSON.stringify(field, null, 2));
        console.log('[EditFieldModal] isPicklistType:', isPicklistType);
        console.log('[EditFieldModal] field.picklistValues:', field.picklistValues);

        if (isPicklistType || field.picklistValues?.length) {
          console.log('[EditFieldModal] Initializing picklist values:', {
            fieldId: field.id,
            fieldName: field.fieldName,
            fieldType: field.fieldType,
            rawPicklistValues: field.picklistValues,
            rawPicklistValuesCount: field.picklistValues?.length,
            normalizedValues: normalizePicklistValues(field.picklistValues)
          });
          setPicklistValues(normalizePicklistValues(field.picklistValues));
        } else {
          console.log('[EditFieldModal] NOT initializing picklist values - no values and not picklist type');
        }
      }
    }
  }, [field, isOpen]);

  const handleUpdate = async () => {
    console.log('[EditFieldModal] ========== SAVE BUTTON CLICKED ==========');
    console.log('[EditFieldModal] field:', field?.id, field?.fieldName);
    console.log('[EditFieldModal] currentObject:', currentObject?.id, currentObject?.apiName);
    console.log('[EditFieldModal] updateFieldMutation.isPending:', updateFieldMutation.isPending);

    if (!field) {
      console.log('[EditFieldModal] EARLY RETURN: field is null/undefined');
      return;
    }
    if (!currentObject) {
      console.warn('[EditFieldModal] currentObject is null. objectIdToUse:', objectIdToUse, 'objects:', objects?.map(o => o.id));
    }

    // PE-1470: Include picklist values with metadata in the update
    // Note: Also check for 'badge' type since some fields (like Stage) are defined as badge but rendered as picklist
    const updatedFormData = { ...formData };
    const isPicklistType = field.fieldType === 'picklist' || field.fieldType === 'multipicklist' || field.fieldType === 'badge';
    if (isPicklistType || picklistValues.length > 0) {
      updatedFormData.picklistValues = picklistValues;
      console.log('[EditFieldModal] Including picklistValues in update (type:', field.fieldType, ')');
    }

    // DEBUG: Log what we're saving
    console.log('[EditFieldModal] ===== SAVING FIELD UPDATE =====');
    console.log('[EditFieldModal] Field ID:', field.id);
    console.log('[EditFieldModal] Field Name:', field.fieldName);
    console.log('[EditFieldModal] Field Type:', field.fieldType);
    console.log('[EditFieldModal] Picklist Values Count:', picklistValues.length);
    console.log('[EditFieldModal] Picklist Values:', JSON.stringify(picklistValues, null, 2));
    console.log('[EditFieldModal] Update Data Keys:', Object.keys(updatedFormData));
    console.log('[EditFieldModal] Has picklistValues in update:', 'picklistValues' in updatedFormData);

    try {
      const result = await updateFieldMutation.mutateAsync({
        fieldId: field.id,
        updates: updatedFormData,
        objectId: objectIdToUse
      });

      // DEBUG: Log the result
      console.log('[EditFieldModal] ===== UPDATE COMPLETED =====');
      console.log('[EditFieldModal] Result ID:', result?.id);
      console.log('[EditFieldModal] Result picklistValues:', JSON.stringify(result?.picklistValues, null, 2));
      console.log('[EditFieldModal] Result picklistValues count:', result?.picklistValues?.length);

      // Verify localStorage was updated correctly
      try {
        const stored = localStorage.getItem('crm_object_fields');
        if (stored) {
          const fields = JSON.parse(stored);
          const savedField = fields.find((f: any) => f.id === field.id);
          console.log('[EditFieldModal] ===== LOCALSTORAGE VERIFICATION =====');
          console.log('[EditFieldModal] Field found in localStorage:', !!savedField);
          console.log('[EditFieldModal] Saved picklistValues:', JSON.stringify(savedField?.picklistValues, null, 2));
          console.log('[EditFieldModal] Saved picklistValues count:', savedField?.picklistValues?.length);
        }
      } catch (e) {
        console.error('[EditFieldModal] Failed to verify localStorage:', e);
      }

      const objectLabel = currentObject?.label || objectIdToUse || 'object';
      logChange({
        action: 'updated',
        entityType: 'field',
        entityName: formData.fieldLabel || field.fieldLabel,
        targetName: `${objectLabel} object`,
        description: `updated field ${formData.fieldLabel || field.fieldLabel} on ${objectLabel} object`,
        performedBy: 'Admin'
      });

      success('Field updated successfully!', `${field.fieldLabel} has been updated`);
      onClose();
    } catch (err: any) {
      console.error('Failed to update field:', err);
      error('Failed to update field', err?.message || 'Please try again');
    }
  };

  const handleDelete = async () => {
    if (!field) return;
    if (deleteInProgressRef.current) return;
    const fieldIdToDelete = field.id;
    const fieldLabelToDelete = field.fieldLabel;
    const objectLabel = currentObject?.label || objectIdToUse || 'object';

    deleteInProgressRef.current = true;
    try {
      await deleteFieldMutation.mutateAsync({
        fieldId: fieldIdToDelete,
        objectId: objectIdToUse,
        objectKey: currentObject?.apiName
      });

      logChange({
        action: 'deleted',
        entityType: 'field',
        entityName: fieldLabelToDelete,
        targetName: `${objectLabel} object`,
        description: `deleted field ${fieldLabelToDelete} from ${objectLabel} object`,
        performedBy: 'Admin'
      });

      success('Field deleted successfully!', `${fieldLabelToDelete} has been permanently removed`);
      onClose();
      setShowDeleteConfirm(false);
    } catch (err: any) {
      console.error('Failed to delete field:', err);
      error('Failed to delete field', err?.message || 'Please try again');
    } finally {
      deleteInProgressRef.current = false;
    }
  };

  if (!isOpen || !field) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Edit Field</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {field.fieldLabel} • {getFieldTypeLabel(field.fieldType)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {field.isStandard && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                <strong>Standard Field:</strong> Some properties cannot be modified for standard fields.
              </p>
            </div>
          )}

          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Basic Information</h3>

              <div className="space-y-4">
                {/* Field Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Field Label
                  </label>
                  <input
                    type="text"
                    value={formData.fieldLabel || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, fieldLabel: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Field Name (Read-only for existing fields) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Field Name (API)
                  </label>
                  <input
                    type="text"
                    value={field.fieldName}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    API names cannot be changed after creation
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Help Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Help Text
                  </label>
                  <input
                    type="text"
                    value={formData.helpText || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, helpText: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Field Type Specific Settings */}
            {field.fieldType === 'text' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Text Field Settings</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Maximum Length
                  </label>
                  <input
                    type="number"
                    value={formData.length || 255}
                    onChange={(e) => setFormData(prev => ({ ...prev, length: parseInt(e.target.value) || 255 }))}
                    min={1}
                    max={255}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum: 255 characters</p>
                </div>
              </div>
            )}

            {field.fieldType === 'textarea' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Text Area Settings</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Maximum Length
                  </label>
                  <input
                    type="number"
                    value={formData.length || 1000}
                    onChange={(e) => setFormData(prev => ({ ...prev, length: parseInt(e.target.value) || 1000 }))}
                    min={1}
                    max={1000}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum: 1,000 characters</p>
                </div>
              </div>
            )}

            {field.fieldType === 'longtextarea' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Long Text Area Settings</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Maximum Length
                  </label>
                  <input
                    type="number"
                    value={formData.length || 32000}
                    onChange={(e) => setFormData(prev => ({ ...prev, length: parseInt(e.target.value) || 32000 }))}
                    min={256}
                    max={32000}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum: 32,000 characters</p>
                </div>
              </div>
            )}

            {/* Picklist Values with Metadata (PE-1469, PE-1470) */}
            {(field.fieldType === 'picklist' || field.fieldType === 'multipicklist') && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {field.fieldType === 'multipicklist' ? 'Multi-Select Picklist Values' : 'Picklist Values'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Configure picklist options. Expand each row to configure auto-fill behavior for other fields.
                </p>

                {/* Picklist Values Table */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    <div className="col-span-1"></div>
                    <div className="col-span-5">Value</div>
                    <div className="col-span-5">Label</div>
                    <div className="col-span-1"></div>
                  </div>

                  {/* Values */}
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {picklistValues.map((pv, index) => {
                      // Get the selected target field for this picklist value
                      const targetFieldName = pv.metadata?.targetField as string | undefined;
                      const targetField = objectFields?.find(f => f.fieldName === targetFieldName);

                      return (
                        <div key={index} className="bg-white dark:bg-gray-800">
                          {/* Main Row */}
                          <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                            {/* Expand Toggle */}
                            <div className="col-span-1">
                              <button
                                type="button"
                                onClick={() => setExpandedMetadata(prev => ({ ...prev, [index]: !prev[index] }))}
                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                title="Configure auto-fill"
                              >
                                {expandedMetadata[index] ? (
                                  <ChevronDown className="w-4 h-4 text-gray-500" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                            </div>

                            {/* Value */}
                            <div className="col-span-5">
                              <input
                                type="text"
                                value={pv.value}
                                onChange={(e) => {
                                  const newValues = [...picklistValues];
                                  newValues[index] = { ...newValues[index], value: e.target.value };
                                  setPicklistValues(newValues);
                                }}
                                placeholder="Value"
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-purple-500"
                              />
                            </div>

                            {/* Label */}
                            <div className="col-span-5">
                              <input
                                type="text"
                                value={pv.label || ''}
                                onChange={(e) => {
                                  const newValues = [...picklistValues];
                                  newValues[index] = { ...newValues[index], label: e.target.value };
                                  setPicklistValues(newValues);
                                }}
                                placeholder="Label (optional)"
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-purple-500"
                              />
                            </div>

                            {/* Delete */}
                            <div className="col-span-1 flex justify-end">
                              <button
                                type="button"
                                onClick={() => {
                                  const newValues = picklistValues.filter((_, i) => i !== index);
                                  setPicklistValues(newValues);
                                }}
                                className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                title="Remove value"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Expanded Metadata Section */}
                          {expandedMetadata[index] && (
                            <div className="px-3 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                              <div className="ml-6 space-y-4">
                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                                  Auto-Fill Configuration
                                </div>

                                {/* Target Field Dropdown */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                    Target Field
                                  </label>
                                  <select
                                    value={targetFieldName || ''}
                                    onChange={(e) => {
                                      const newValues = [...picklistValues];
                                      const selectedFieldName = e.target.value || undefined;
                                      newValues[index] = {
                                        ...newValues[index],
                                        metadata: {
                                          ...newValues[index].metadata,
                                          targetField: selectedFieldName,
                                          // Clear defaultValue when target field changes
                                          defaultValue: selectedFieldName ? newValues[index].metadata?.defaultValue : undefined
                                        }
                                      };
                                      setPicklistValues(newValues);
                                    }}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-purple-500"
                                  >
                                    <option value="">-- Select target field --</option>
                                    {objectFields
                                      ?.filter(f => f.fieldName !== field.fieldName) // Exclude current field
                                      .map(f => (
                                        <option key={f.id} value={f.fieldName}>
                                          {f.fieldLabel} ({f.fieldName})
                                        </option>
                                      ))
                                    }
                                  </select>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Select the field that should be auto-filled when this picklist value is selected
                                  </p>
                                </div>

                                {/* Default Value - Dynamic based on target field type */}
                                {targetFieldName && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                      Default Value for {targetField?.fieldLabel || targetFieldName}
                                    </label>
                                    {/* Render input based on target field type */}
                                    {targetField?.fieldType === 'checkbox' ? (
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={pv.metadata?.defaultValue === true || pv.metadata?.defaultValue === 'true'}
                                          onChange={(e) => {
                                            const newValues = [...picklistValues];
                                            newValues[index] = {
                                              ...newValues[index],
                                              metadata: {
                                                ...newValues[index].metadata,
                                                defaultValue: e.target.checked
                                              }
                                            };
                                            setPicklistValues(newValues);
                                          }}
                                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">
                                          {pv.metadata?.defaultValue === true || pv.metadata?.defaultValue === 'true' ? 'True' : 'False'}
                                        </span>
                                      </div>
                                    ) : targetField?.fieldType === 'number' || targetField?.fieldType === 'currency' || targetField?.fieldType === 'percent' ? (
                                      <input
                                        type="number"
                                        value={pv.metadata?.defaultValue ?? ''}
                                        onChange={(e) => {
                                          const newValues = [...picklistValues];
                                          const numVal = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                          newValues[index] = {
                                            ...newValues[index],
                                            metadata: {
                                              ...newValues[index].metadata,
                                              defaultValue: numVal
                                            }
                                          };
                                          setPicklistValues(newValues);
                                        }}
                                        placeholder={targetField?.fieldType === 'percent' ? '0-100' : 'Enter number'}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-purple-500"
                                      />
                                    ) : targetField?.fieldType === 'date' ? (
                                      <input
                                        type="date"
                                        value={pv.metadata?.defaultValue || ''}
                                        onChange={(e) => {
                                          const newValues = [...picklistValues];
                                          newValues[index] = {
                                            ...newValues[index],
                                            metadata: {
                                              ...newValues[index].metadata,
                                              defaultValue: e.target.value || undefined
                                            }
                                          };
                                          setPicklistValues(newValues);
                                        }}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-purple-500"
                                      />
                                    ) : targetField?.fieldType === 'picklist' ? (
                                      <select
                                        value={pv.metadata?.defaultValue || ''}
                                        onChange={(e) => {
                                          const newValues = [...picklistValues];
                                          newValues[index] = {
                                            ...newValues[index],
                                            metadata: {
                                              ...newValues[index].metadata,
                                              defaultValue: e.target.value || undefined
                                            }
                                          };
                                          setPicklistValues(newValues);
                                        }}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-purple-500"
                                      >
                                        <option value="">-- Select value --</option>
                                        {targetField.picklistValues?.map(v => {
                                          const val = typeof v === 'string' ? v : v.value;
                                          const label = typeof v === 'string' ? v : (v.label || v.value);
                                          return (
                                            <option key={val} value={val}>{label}</option>
                                          );
                                        })}
                                      </select>
                                    ) : (
                                      <input
                                        type="text"
                                        value={pv.metadata?.defaultValue || ''}
                                        onChange={(e) => {
                                          const newValues = [...picklistValues];
                                          newValues[index] = {
                                            ...newValues[index],
                                            metadata: {
                                              ...newValues[index].metadata,
                                              defaultValue: e.target.value || undefined
                                            }
                                          };
                                          setPicklistValues(newValues);
                                        }}
                                        placeholder="Enter default value"
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-purple-500"
                                      />
                                    )}
                                    <p className="text-xs text-gray-500 mt-1">
                                      This value will be auto-filled into {targetField?.fieldLabel || targetFieldName} when "{pv.value || 'this value'}" is selected
                                    </p>
                                  </div>
                                )}

                                {/* Custom Key-Value JSON */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                    Custom Metadata (JSON)
                                  </label>
                                  <input
                                    type="text"
                                    value={(() => {
                                      const filtered = Object.fromEntries(
                                        Object.entries(pv.metadata || {}).filter(([k]) => !['targetField', 'defaultValue'].includes(k))
                                      );
                                      return Object.keys(filtered).length > 0 ? JSON.stringify(filtered) : '';
                                    })()}
                                    onChange={(e) => {
                                      try {
                                        const customMeta = e.target.value ? JSON.parse(e.target.value) : {};
                                        const newValues = [...picklistValues];
                                        newValues[index] = {
                                          ...newValues[index],
                                          metadata: {
                                            targetField: newValues[index].metadata?.targetField,
                                            defaultValue: newValues[index].metadata?.defaultValue,
                                            ...customMeta
                                          }
                                        };
                                        setPicklistValues(newValues);
                                      } catch {
                                        // Invalid JSON, ignore
                                      }
                                    }}
                                    placeholder='{"key": "value"}'
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-purple-500 font-mono"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    Additional custom metadata as JSON (optional)
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Empty State */}
                    {picklistValues.length === 0 && (
                      <div className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                        No picklist values defined. Add values below.
                      </div>
                    )}
                  </div>

                  {/* Add New Value */}
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                    <button
                      type="button"
                      onClick={() => {
                        setPicklistValues([...picklistValues, { value: '', label: '' }]);
                      }}
                      className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                    >
                      <Plus className="w-4 h-4" />
                      Add Value
                    </button>
                  </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {picklistValues.filter(v => v.value.trim()).length} value(s) defined
                </p>
              </div>
            )}

            {/* Field Options */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Field Options</h3>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.required || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                    disabled={field.isStandard}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Required</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.unique || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, unique: e.target.checked }))}
                    disabled={field.isStandard}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Unique</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isPII || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, isPII: e.target.checked }))}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Contains PII</span>
                </label>
              </div>
            </div>

            {/* Lookup Relationship Info */}
            {(field.fieldType === 'lookup' || field.fieldType === 'masterdetail') && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {field.fieldType === 'masterdetail' ? 'Master-Detail Relationship' : 'Lookup Relationship'}
                </h3>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  {field.lookupObject ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Related Object
                      </label>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium">
                          {objects?.find(obj => obj.apiName === field.lookupObject)?.label || field.lookupObject}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          ({field.lookupObject})
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        {field.fieldType === 'masterdetail' 
                          ? 'This field creates a master-detail relationship. Deleting the parent record will delete all child records.'
                          : 'This field creates a lookup relationship to the related object.'}
                      </p>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <p className="font-medium mb-1">Related object not specified</p>
                      <p className="text-xs">This lookup field does not have a target object configured.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Default Value */}
            {field.fieldType !== 'checkbox' && field.fieldType !== 'lookup' && field.fieldType !== 'masterdetail' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Default Value</h3>
                <input
                  type="text"
                  value={formData.defaultValue || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, defaultValue: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Field-Level Security */}
            {currentObject && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <FieldSecurityManager
                  fieldId={field.id}
                  fieldLabel={field.fieldLabel}
                  objectName={currentObject.apiName}
                  mode="compact"
                />
              </div>
            )}

            {/* Delete Field Section */}
            {!field.isStandard && (
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Danger Zone</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Deleting this field will remove it from all records. This action cannot be undone.
                </p>
                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete Field
                  </button>
                ) : (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-4">
                      <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-900 dark:text-red-100 mb-1">Are you sure?</p>
                        <p className="text-sm text-red-800 dark:text-red-300">
                          This will permanently delete the field "{field.fieldLabel}" and all its data.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={handleDelete}
                        disabled={deleteFieldMutation.isPending}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        {deleteFieldMutation.isPending ? 'Deleting...' : 'Yes, Delete Field'}
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpdate}
            disabled={updateFieldMutation.isPending}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {updateFieldMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
